import React, { useState, useMemo, Fragment, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import Modal from 'react-modal';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import AssessmentQuestionPreview from '../../../containers/assessment/AssessmentQuestionPreview';
import { TextDefault, TextLead, questionSupportsAnswerHighlight } from '@awarego-ui/components';
import PreviewModalHeader from '../../../containers/previewModalHeader';
import PreviewQuestionDetails from './preview-question-details';
import QuestionPreviewTabbedContent from './QuestionPreviewTabbedContent';
import { useStores } from '../../../hooks/use-stores';
import { useTranslation } from 'react-i18next';
import './preview-question.scss';
import { refreshI18nPreview } from '../../../utils/refreshI18nPreview';
import {
    getStoredHraPreviewLanguage,
    resolveHraPreviewLanguage,
    applyHraPreviewLanguage,
    filterPreviewLanguages,
} from '../../../utils/hraPreviewLanguage';

function QuestionPreviewModal({ question, companyId, onClose }) {
    const { i18n } = useTranslation('assessment');
    const { store, brandingStore } = useStores();
    const partnerId = companyId ? null : store.currentCompany?.account_id;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
        getStoredHraPreviewLanguage(
            (question.languages && question.languages[0]) || 'en'
        )
    );
    const [translationOverridesReady, setTranslationOverridesReady] =
        useState(!question.customizable);
    const [highlightCorrectAnswers, setHighlightCorrectAnswers] =
        useState(false);
    const [languageReady, setLanguageReady] = useState(false);

    const previewPayload = useMemo(() => {
        const ui =
            question.ui ||
            question.definition?.ui || {
                blocks: [],
                presentation: null,
                minimumBlockCount: 1,
            };
        const actions = question.actions || question.definition?.actions;
        const id = question.id != null ? String(question.id) : 'preview-draft';
        return {
            ...question,
            id,
            ui,
            actions,
            definition: {
                ...(question.definition || {}),
                ui,
                actions,
            },
        };
    }, [question]);

    const previewLanguages = useMemo(() => {
        const langs = previewPayload.languages || ['en'];
        const enabled = companyId ? store.enabledLanguages : [];
        return filterPreviewLanguages(langs, enabled, langs[0] || 'en');
    }, [
        previewPayload.languages,
        companyId,
        store.currentCompany?.languages,
    ]);

    const customizeSaveTo = companyId
        ? 'company'
        : previewPayload.customizeSaveTo || 'company';

    const detailsQuestion = useMemo(
        () => ({
            ...previewPayload,
            behaviours: previewPayload.behaviours || [],
            adminDescription: previewPayload.adminDescription || '',
            difficulty: previewPayload.difficulty || 'basic',
        }),
        [previewPayload]
    );

    const questionDetails = useMemo(
        () => (
            <>
                <Link
                    component="button"
                    type="button"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    <TextDefault themed bold flex gap={2} link>
                        <InfoIcon fontSize="small" />
                        Scenario details
                    </TextDefault>
                </Link>
                <TextLead>{previewPayload.name}</TextLead>
            </>
        ),
        [previewPayload.name]
    );

    const handleSelectLanguage = useCallback(
        async (languageKey) => {
            setLanguageReady(false);
            setSelectedLanguage(languageKey);
            await applyHraPreviewLanguage(i18n, languageKey, {
                persist: true,
                refreshOverrides:
                    Boolean(previewPayload.customizable) &&
                    translationOverridesReady,
            });
            setLanguageReady(true);
        },
        [
            i18n,
            previewPayload.customizable,
            translationOverridesReady,
        ]
    );

    useEffect(() => {
        const resolved = resolveHraPreviewLanguage(
            previewLanguages,
            previewLanguages[0] || 'en'
        );
        void handleSelectLanguage(resolved);
    }, [previewPayload.id, previewLanguages, handleSelectLanguage]);

    useEffect(() => {
        if (!translationOverridesReady || !languageReady || !previewPayload.customizable) {
            return;
        }
        void refreshI18nPreview();
    }, [
        translationOverridesReady,
        languageReady,
        previewPayload.customizable,
    ]);

    useEffect(() => {
        if (!previewPayload.customizable) {
            setTranslationOverridesReady(true);
            return;
        }
        let cancelled = false;
        setTranslationOverridesReady(false);
        (async () => {
            await brandingStore.ensureQuestionTranslationOverrides({
                partnerId,
                companyId,
                customizeSaveTo,
            });
            if (!cancelled) {
                setTranslationOverridesReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        previewPayload.customizable,
        customizeSaveTo,
        partnerId,
        companyId,
        brandingStore,
    ]);

    return (
        <Modal
            isOpen
            onRequestClose={onClose}
            className="preview-questions-modal"
            overlayClassName="preview-questions-overlay"
            shouldCloseOnOverlayClick
        >
            <Fragment>
                <div className="question-preview-shell">
                    <PreviewModalHeader
                        titlePrefix="Preview:"
                        title={previewPayload.name}
                        showLanguageSelector
                        availableLanguages={previewLanguages}
                        onClose={onClose}
                        onSelectLanguage={handleSelectLanguage}
                        selectedLanguage={selectedLanguage}
                        details={questionDetails}
                        showHighlightCorrectAnswers={questionSupportsAnswerHighlight(
                            previewPayload
                        )}
                        highlightCorrectAnswers={highlightCorrectAnswers}
                        onHighlightCorrectAnswersChange={
                            setHighlightCorrectAnswers
                        }
                    />
                    <QuestionPreviewTabbedContent
                        customizable={Boolean(previewPayload.customizable)}
                        translationOverridesReady={translationOverridesReady}
                        language={selectedLanguage}
                        companyId={companyId}
                        partnerId={partnerId}
                        customizeSaveTo={customizeSaveTo}
                        definition={previewPayload}
                        questionId={previewPayload.id}
                        questionName={previewPayload.name}
                        previewContent={
                            <AssessmentQuestionPreview
                                companyId={companyId}
                                question={previewPayload}
                                isReview={false}
                                questionsSelected={[previewPayload.id]}
                                selectedQuestionId={previewPayload.id}
                                setSelectedQuestionId={() => {}}
                                hasMoreQuestions={false}
                                isMovingBack={false}
                                setIsMovingBack={() => {}}
                                onRequestClose={onClose}
                                loadingData={!languageReady}
                                isWizard={false}
                                hideFooter
                                highlightCorrectAnswers={highlightCorrectAnswers}
                            />
                        }
                    />
                </div>
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    elevation={1}
                >
                    <PreviewQuestionDetails
                        handleQuestionDetailsClose={() => setAnchorEl(null)}
                        question={detailsQuestion}
                    />
                </Popover>
            </Fragment>
        </Modal>
    );
}

export default observer(QuestionPreviewModal);
